import axios from 'axios';
import { OktaAuth } from '@okta/okta-auth-js';
import { apiConfig } from '../config';
import type {
  ApiResponse, CreateAndRunResult, HistoryListResponse,
  SDQRunHistory, ProfilingSessionHistory, UDQAssessmentHistory,
  MappingRunListItem, MappingRunDetail,
} from '../types';

const api = axios.create({
  baseURL: apiConfig.baseUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 180000,  // 3 minutes — agent turns (esp. pushdown assessment) can be long
});

let oktaAuthInstance: OktaAuth | null = null;

export function setOktaAuth(auth: OktaAuth) {
  oktaAuthInstance = auth;
}

/* Attach Bearer token to every request */
api.interceptors.request.use(async (config) => {
  if (oktaAuthInstance) {
    const token = oktaAuthInstance.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

/* Global error handler */
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      oktaAuthInstance?.signInWithRedirect();
    }
    return Promise.reject(err);
  },
);

/* ───────── Onboarding endpoints ───────── */

export const onboardingApi = {
  testConnection: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/onboarding/test-connection', payload),

  loadTestCredentials: () =>
    api.get<ApiResponse>('/api/onboarding/test-credentials'),

  listSchemas: (payload: Record<string, any>) =>
    api.post<ApiResponse<string[]>>('/api/onboarding/list-schemas', payload),

  listTables: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/onboarding/list-tables', payload),

  tableColumns: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/onboarding/table-columns', payload),

  autoGenerateRules: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/onboarding/auto-generate-rules', payload),

  createAndRun: (payload: Record<string, any>) =>
    api.post<ApiResponse<CreateAndRunResult>>('/api/onboarding/create-and-run', payload),

  saveProject: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/onboarding/save-project', payload),

  profileSource: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/onboarding/profile-source', payload),

  generateDictionary: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/onboarding/generate-dictionary', payload),
};

/* ───────── Projects & Groups ───────── */

export const projectsApi = {
  list: (params?: Record<string, any>) =>
    api.get<ApiResponse>('/api/projects', { params }),

  get: (id: number) =>
    api.get<ApiResponse>(`/api/projects/${id}`),

  groups: (projectId: number, params?: Record<string, any>) =>
    api.get<ApiResponse>(`/api/projects/${projectId}/groups`, { params }),
};

/* ───────── SDQ ───────── */

export const sdqApi = {
  run: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/sdq/run', payload),

  history: (params?: Record<string, any>) =>
    api.get<ApiResponse<HistoryListResponse<SDQRunHistory>>>('/api/sdq/history', { params }),

  historyDetail: (runId: string) =>
    api.get<ApiResponse>(`/api/sdq/history/${runId}`),

  saveHistory: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/sdq/history', payload),
};

/* ───────── UDQ ───────── */

export const udqApi = {
  assessDocument: (formData: FormData) =>
    api.post<ApiResponse>('/api/udq/assess', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  history: (params?: Record<string, any>) =>
    api.get<ApiResponse<HistoryListResponse<UDQAssessmentHistory>>>('/api/udq/history', { params }),

  historyDetail: (assessmentId: string) =>
    api.get<ApiResponse>(`/api/udq/history/${assessmentId}`),
};

/* ───────── Profiling History ───────── */

export const profilingApi = {
  history: (params?: Record<string, any>) =>
    api.get<ApiResponse<HistoryListResponse<ProfilingSessionHistory>>>('/api/profiling/history', { params }),

  historyDetail: (sessionId: string) =>
    api.get<ApiResponse>(`/api/profiling/history/${sessionId}`),

  saveHistory: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/profiling/history', payload),
};

/* ───────── Other ───────── */

export const dashboardApi = {
  list: (params?: Record<string, any>) =>
    api.get<ApiResponse>('/api/dashboard', { params }),

  project: (projectId: number, params?: Record<string, any>) =>
    api.get<ApiResponse>(`/api/dashboard/${projectId}`, { params }),

  validationResult: (resultId: number) =>
    api.get<ApiResponse>(`/api/validation-result/${resultId}`),
};

export const rulesApi = {
  list: (params?: Record<string, any>) =>
    api.get<ApiResponse>('/api/rules', { params }),

  requestRule: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/requestrule', payload),
};

/* ───────── Groups CRUD ───────── */

export const groupsApi = {
  list: (params?: Record<string, any>) =>
    api.get<ApiResponse>('/api/groups', { params }),

  getRules: (groupId: number, params?: Record<string, any>) =>
    api.get<ApiResponse>(`/api/groups/${groupId}/rules`, { params }),

  addRules: (groupId: number, payload: Record<string, any>) =>
    api.post<ApiResponse>(`/api/groups/${groupId}/rules`, payload),

  editRule: (ruleId: number, payload: Record<string, any>) =>
    api.put<ApiResponse>(`/api/groups/rules/${ruleId}`, payload),

  deleteRule: (ruleId: number, params: Record<string, any>) =>
    api.delete<ApiResponse>(`/api/groups/rules/${ruleId}`, { params }),
};

/* ───────── Observability endpoints ───────── */

export const observabilityApi = {
  healthSummary: () =>
    api.get<any>('/api/observability/health-summary'),

  dashboardMetrics: (projectId?: number) =>
    api.get<any>('/api/observability/dashboard-metrics', {
      params: projectId ? { project_id: projectId } : undefined,
    }),

  listTables: (params?: Record<string, any>) =>
    api.get<any>('/api/observability/tables', { params }),

  registerTable: (payload: Record<string, any>) =>
    api.post<any>('/api/observability/tables', payload),

  unregisterTable: (id: number) =>
    api.delete<any>(`/api/observability/tables/${id}`),

  getTableBundle: (id: number) =>
    api.get<any>(`/api/observability/tables/${id}/metrics`),

  getMetrics: (params: Record<string, any>) =>
    api.get<any>('/api/observability/metrics', { params }),

  listAnomalies: (params?: Record<string, any>) =>
    api.get<any>('/api/observability/anomalies', { params }),

  ackAnomaly: (id: number) =>
    api.post<any>(`/api/observability/anomalies/${id}/ack`),

  listIncidents: (params?: Record<string, any>) =>
    api.get<any>('/api/observability/incidents', { params }),

  getIncident: (id: number) =>
    api.get<any>(`/api/observability/incidents/${id}`),

  updateIncident: (id: number, payload: Record<string, any>) =>
    api.patch<any>(`/api/observability/incidents/${id}`, payload),

  lineage: (projectId?: number) =>
    api.get<any>('/api/observability/lineage', {
      params: projectId ? { project_id: projectId } : undefined,
    }),

  observedProjects: () =>
    api.get<any>('/api/observability/projects'),

  discoverLineage: () =>
    api.post<any>('/api/observability/lineage/discover'),
};

/* ───────── Schema Mapping ───────── */

export const mappingApi = {
  testConnection: (payload: Record<string, any>) =>
    api.post<ApiResponse<boolean>>('/api/mapping/test-connection', payload),

  listSchemas: (payload: Record<string, any>) =>
    api.post<ApiResponse<string[]>>('/api/mapping/list-schemas', payload),

  listTables: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/mapping/list-tables', payload),

  generateDictionary: (payload: Record<string, any>) =>
    api.post<ApiResponse>('/api/mapping/generate-dictionary', payload),

  createRun: (payload: Record<string, any>) =>
    api.post<ApiResponse<{ run_id: string; status: string }>>('/api/mapping/runs', payload),

  listRuns: (params?: Record<string, any>) =>
    api.get<ApiResponse<{ runs: MappingRunListItem[]; total: number; limit: number; skip: number }>>(
      '/api/mapping/runs', { params },
    ),

  getRun: (runId: string) =>
    api.get<ApiResponse<MappingRunDetail>>(`/api/mapping/runs/${runId}`),

  approveRun: (runId: string, payload: { action: 'approve' | 'reject'; approved_by: string; rejection_reason?: string }) =>
    api.post<ApiResponse>(`/api/mapping/runs/${runId}/approval`, payload),

  deleteRun: (runId: string) =>
    api.delete<ApiResponse>(`/api/mapping/runs/${runId}`),
};

/* ───────── Profiling Agent (onboarding surface) ───────── */

export const profilingAgentApi = {
  createSession: (payload?: { initial_message?: string; title?: string }) =>
    api.post<ApiResponse<any>>('/api/profiling-agent/sessions', payload ?? {}),

  listSessions: (params?: Record<string, any>) =>
    api.get<ApiResponse<{ sessions: any[]; total: number; limit: number; skip: number }>>(
      '/api/profiling-agent/sessions', { params },
    ),

  getSession: (sessionId: string) =>
    api.get<ApiResponse<any>>(`/api/profiling-agent/sessions/${sessionId}`),

  deleteSession: (sessionId: string) =>
    api.delete<ApiResponse>(`/api/profiling-agent/sessions/${sessionId}`),
};

/* ───────── Data Content Profiler (standalone agent) ───────── */

export const dataContentProfilerApi = {
  createSession: (payload?: { initial_message?: string; title?: string }) =>
    api.post<ApiResponse<any>>('/api/data-content-profiler/sessions', payload ?? {}),

  listSessions: (params?: Record<string, any>) =>
    api.get<ApiResponse<{ sessions: any[]; total: number; limit: number; skip: number }>>(
      '/api/data-content-profiler/sessions', { params },
    ),

  getSession: (sessionId: string) =>
    api.get<ApiResponse<any>>(`/api/data-content-profiler/sessions/${sessionId}`),

  deleteSession: (sessionId: string) =>
    api.delete<ApiResponse>(`/api/data-content-profiler/sessions/${sessionId}`),
};

/* ───────── Schema Mapping Agent (standalone agent) ───────── */

export const schemaMappingAgentApi = {
  createSession: (payload?: { initial_message?: string; title?: string }) =>
    api.post<ApiResponse<any>>('/api/schema-mapping-agent/sessions', payload ?? {}),

  listSessions: (params?: Record<string, any>) =>
    api.get<ApiResponse<{ sessions: any[]; total: number; limit: number; skip: number }>>(
      '/api/schema-mapping-agent/sessions', { params },
    ),

  getSession: (sessionId: string) =>
    api.get<ApiResponse<any>>(`/api/schema-mapping-agent/sessions/${sessionId}`),

  deleteSession: (sessionId: string) =>
    api.delete<ApiResponse>(`/api/schema-mapping-agent/sessions/${sessionId}`),
};

/* ───────── Incident Analyst (investigation surface) ───────── */

export const incidentAnalystApi = {
  createSession: (payload?: { initial_message?: string; title?: string }) =>
    api.post<ApiResponse<any>>('/api/incident-analyst/sessions', payload ?? {}),

  listSessions: (params?: Record<string, any>) =>
    api.get<ApiResponse<{ sessions: any[]; total: number; limit: number; skip: number }>>(
      '/api/incident-analyst/sessions', { params },
    ),

  getSession: (sessionId: string) =>
    api.get<ApiResponse<any>>(`/api/incident-analyst/sessions/${sessionId}`),

  deleteSession: (sessionId: string) =>
    api.delete<ApiResponse>(`/api/incident-analyst/sessions/${sessionId}`),
};

export default api;
